package com.evoapps.evoshift.viewmodels

import evoshift.composeapp.generated.resources.contract_owner_partner
import com.evoapps.evoshift.domain.model.Adjustment
import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry
import com.evoapps.evoshift.domain.model.AdjustmentType
import com.evoapps.evoshift.domain.model.AuditMetadata
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.finance.AdjustmentInput
import com.evoapps.evoshift.domain.usecase.finance.DeleteAdjustmentUseCase
import com.evoapps.evoshift.domain.usecase.finance.SaveAdjustmentUseCase
import evoshift.composeapp.generated.resources.adjustment_deleted
import evoshift.composeapp.generated.resources.adjustment_saved
import evoshift.composeapp.generated.resources.export_col_bonuses
import evoshift.composeapp.generated.resources.export_col_deductions
import evoshift.composeapp.generated.resources.export_col_refunds
import evoshift.composeapp.generated.resources.export_col_overtime_50
import evoshift.composeapp.generated.resources.export_col_overtime_100
import evoshift.composeapp.generated.resources.export_col_sunday_holiday
import evoshift.composeapp.generated.resources.export_col_supplements
import evoshift.composeapp.generated.resources.export_col_minimum_top_up
import evoshift.composeapp.generated.resources.export_col_sick_days
import evoshift.composeapp.generated.resources.export_col_sick_pay
import evoshift.composeapp.generated.resources.export_col_vacation_days
import evoshift.composeapp.generated.resources.export_col_holiday_pay
import evoshift.composeapp.generated.resources.export_col_planned_gross
import evoshift.composeapp.generated.resources.export_col_employer_cost_plan
import evoshift.composeapp.generated.resources.export_col_employer_cost
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.drop
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.RosterPayroll
import com.evoapps.evoshift.domain.usecase.financial.GeneratePayrollReportUseCase
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.ui.finance.HoursExport
import com.evoapps.evoshift.ui.finance.HoursExportLabels
import com.evoapps.evoshift.ui.reports.FileSaver
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import com.evoapps.evoshift.util.appLanguage
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.contract_b2b
import evoshift.composeapp.generated.resources.contract_civil
import evoshift.composeapp.generated.resources.contract_employment
import evoshift.composeapp.generated.resources.contract_specific_task
import evoshift.composeapp.generated.resources.employee_not_set
import evoshift.composeapp.generated.resources.export_col_clocked_hours
import evoshift.composeapp.generated.resources.export_col_contract
import evoshift.composeapp.generated.resources.export_col_currency
import evoshift.composeapp.generated.resources.export_col_employee
import evoshift.composeapp.generated.resources.export_col_gross
import evoshift.composeapp.generated.resources.export_col_month
import evoshift.composeapp.generated.resources.export_col_paid_hours
import evoshift.composeapp.generated.resources.export_col_planned_hours
import evoshift.composeapp.generated.resources.labour_cost_exported
import org.jetbrains.compose.resources.getString
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class PayrollUiState(
    val isLoading: Boolean = true,
    val payroll: RosterPayroll? = null,
    val expandedUserId: String? = null,
    /** What the person may do at the roster's workplace: the labour cost needs PAYROLL. */
    val capabilities: Set<Capability> = emptySet(),
    val error: UiText? = null,
    /** A one-off note for the snackbar ("Exported …", "Bonus or deduction saved", why a save failed). */
    val message: UiText? = null,
    /** SH-188: the first read failed (nothing on screen): Try again instead of an empty screen. */
    val loadError: UiText? = null
) {
    /**
     * Bonuses, deductions and refunds can be added, changed and removed (SH-78): owners and
     * financial managers while the plan runs (SH-65), until the month is LOCKED.
     */
    val canEditAdjustments: Boolean
        get() = capabilities.shows(Gate.ADJUSTMENTS_EDITING) && payroll?.locked == false
}

/**
 * "Labour cost" of a roster month (owner, financial managers): the estimated gross, take-home
 * and employer cost per person, their bonuses, deductions and refunds (SH-78), and the hours
 * export for the accountant (SH-56). Estimates for planning, never the pay run.
 */
class PayrollViewModel(
    private val rosterId: String,
    private val generatePayrollReport: GeneratePayrollReportUseCase,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val fileSaver: FileSaver,
    private val saveAdjustment: SaveAdjustmentUseCase? = null,
    private val deleteAdjustment: DeleteAdjustmentUseCase? = null,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    /** SH-147: pay terms set (or someone confirmed as unpaid) elsewhere: the month is read again. */
    private val payTermsChanges: com.evoapps.evoshift.domain.budget.PayTermsChanges? = null
) : ViewModel() {
    private val _uiState = MutableStateFlow(PayrollUiState())
    val uiState = _uiState.asStateFlow()

    /** Who is signed in: the author of the adjustments they save. */
    private var editorId: String? = null

    /** Adjustment saves still running: a quiet refresh waits for the last one. */
    private var savesInFlight = 0

    init {
        load()
        payTermsChanges?.let { changes ->
            viewModelScope.launch { changes.version.drop(1).collect { load() } }
        }
    }

    fun load() {
        viewModelScope.launch {
            // A reload keeps the figures on screen until the new ones arrive.
            _uiState.update { it.copy(isLoading = it.payroll == null, error = null) }
            val user = getCurrentUser().first()
            editorId = user?.uid
            val workplaceId = RosterIds.workplaceIdOf(rosterId)
            // Read-only once the workplace's plan ended (SH-65): adjustments are seen, not changed.
            val capabilities = user?.capabilitiesAt(workplaceId, observePlanActive.activeNow(workplaceId)).orEmpty()
            _uiState.update { it.copy(capabilities = capabilities) }
            // Pay terms and labour cost are the owner's and financial managers': nothing is read otherwise.
            if (!capabilities.shows(Gate.PAYROLL)) {
                _uiState.update { it.copy(isLoading = false, payroll = null) }
                return@launch
            }
            generatePayrollReport(rosterId).fold(
                onSuccess = { payroll -> _uiState.update { it.copy(isLoading = false, payroll = payroll, loadError = null) } },
                // SH-188: nothing on screen yet: Try again; over the figures shown, a message.
                onFailure = { e ->
                    _uiState.update {
                        if (it.payroll == null) it.copy(isLoading = false, loadError = e.toUiText())
                        else it.copy(isLoading = false, error = e.toUiText())
                    }
                }
            )
        }
    }

    fun toggle(userId: String) = _uiState.update {
        it.copy(expandedUserId = if (it.expandedUserId == userId) null else userId)
    }

    /**
     * Hours and estimated gross per person for the month as a CSV ([HoursExport]), handed to the
     * platform's [FileSaver]: what the accountant needs to run the pay in their own software.
     */
    fun exportHours() {
        val state = _uiState.value
        val payroll = state.payroll ?: return
        if (!state.capabilities.shows(Gate.PAYROLL)) return
        viewModelScope.launch {
            runCatching {
                val file = HoursExport.file(payroll, exportLabels())
                fileSaver.save(file)
                file.name
            }.fold(
                onSuccess = { name -> _uiState.update { it.copy(message = UiText.Resource(Res.string.labour_cost_exported, listOf(name))) } },
                onFailure = { e -> _uiState.update { it.copy(message = e.toUiText()) } }
            )
        }
    }

    fun messageShown() = _uiState.update { it.copy(message = null) }

    // --- Bonuses, deductions and expense refunds (SH-78) ---------------------------------------

    /**
     * Adds [userId]'s adjustment, or changes one ([editingId]). Optimistic: it shows in their list
     * and sums at once (a placeholder id while new); a failed save puts the month back and says why.
     * The estimate around it (tax on a bonus) is worked out again once saved.
     */
    fun saveAdjustment(userId: String, input: AdjustmentInput, editingId: String? = null) {
        val useCase = saveAdjustment ?: return
        val editor = editorId ?: return
        val before = _uiState.value
        val payroll = before.payroll ?: return
        if (!before.canEditAdjustments || editingId?.isPending == true) return
        val employee = payroll.employees.firstOrNull { it.userId == userId } ?: return
        val previous = editingId?.let { id -> employee.adjustments.firstOrNull { it.id == id } }
        val shown = Adjustment(
            id = editingId ?: "$PENDING_ID_PREFIX${employee.adjustments.size}-${input.description}",
            userId = userId,
            rosterId = rosterId,
            type = input.type,
            amount = input.amount,
            currency = payroll.currency,
            description = input.description.trim(),
            date = input.date,
            relatedShiftId = input.relatedShiftId,
            metadata = previous?.metadata ?: AuditMetadata(createdAt = "", createdBy = editor)
        )
        updateAdjustments(userId) { list -> list.filterNot { it.id == shown.id } + shown }
        savesInFlight++
        viewModelScope.launch {
            useCase(rosterId, userId, input, editor, editingId).fold(
                onSuccess = { saved ->
                    updateAdjustments(userId) { list -> list.map { if (it.id == shown.id) saved else it } }
                    if (editingId == null) AppTelemetry.log(AnalyticsEvent.AdjustmentAdded)
                    _uiState.update { it.copy(message = UiText.Resource(Res.string.adjustment_saved)) }
                },
                onFailure = { e -> rollBack(before, e) }
            )
            savesInFlight--
            refreshQuietly()
        }
    }

    /** Removes [userId]'s adjustment [adjustmentId]; optimistic, put back when the delete fails. */
    fun deleteAdjustment(userId: String, adjustmentId: String) {
        val useCase = deleteAdjustment ?: return
        val before = _uiState.value
        if (!before.canEditAdjustments || adjustmentId.isPending) return
        val employee = before.payroll?.employees?.firstOrNull { it.userId == userId } ?: return
        if (employee.adjustments.none { it.id == adjustmentId }) return
        updateAdjustments(userId) { list -> list.filterNot { it.id == adjustmentId } }
        savesInFlight++
        viewModelScope.launch {
            useCase(rosterId, adjustmentId).fold(
                onSuccess = { _uiState.update { it.copy(message = UiText.Resource(Res.string.adjustment_deleted)) } },
                onFailure = { e -> rollBack(before, e) }
            )
            savesInFlight--
            refreshQuietly()
        }
    }

    /** [userId]'s adjustments changed by [change], with their sums (the payslip waits for the refresh). */
    private fun updateAdjustments(userId: String, change: (List<Adjustment>) -> List<Adjustment>) = _uiState.update { state ->
        val payroll = state.payroll ?: return@update state
        state.copy(payroll = payroll.copy(employees = payroll.employees.map { e ->
            if (e.userId != userId) return@map e
            val list = change(e.adjustments).sortedBy { it.date }
            fun sum(type: AdjustmentType) = list.filter { it.type == type }.sumOf { it.amount }
            e.copy(
                adjustments = list,
                bonuses = sum(AdjustmentType.BONUS),
                deductions = sum(AdjustmentType.DEDUCTION),
                expenseRefunds = sum(AdjustmentType.EXPENSE_REFUND)
            )
        }))
    }

    /** SH-188: Try again after the first read failed. */
    fun retry() {
        _uiState.update { it.copy(loadError = null) }
        load()
    }

    private fun rollBack(before: PayrollUiState, e: Throwable) {
        _uiState.update { it.copy(payroll = before.payroll, message = e.toUiText()) }
    }

    /** The estimate worked out again behind the figures on screen (no spinner), once no save is running. */
    private suspend fun refreshQuietly() {
        if (savesInFlight > 0) return
        generatePayrollReport(rosterId).onSuccess { payroll ->
            if (savesInFlight == 0) _uiState.update { it.copy(payroll = payroll) }
        }
    }

    private suspend fun exportLabels(): HoursExportLabels {
        val contracts = mapOf(
            SystemContractType.STANDARD_EMPLOYMENT to getString(Res.string.contract_employment),
            SystemContractType.NOT_SET to getString(Res.string.employee_not_set),
            SystemContractType.CIVIL_LAW_CONTRACT to getString(Res.string.contract_civil),
            SystemContractType.SPECIFIC_TASK_CONTRACT to getString(Res.string.contract_specific_task),
            SystemContractType.B2B_CONTRACTOR to getString(Res.string.contract_b2b),
            SystemContractType.OWNER_PARTNER to getString(Res.string.contract_owner_partner)
        )
        return HoursExportLabels(
            language = appLanguage(),
            month = getString(Res.string.export_col_month),
            employee = getString(Res.string.export_col_employee),
            contract = getString(Res.string.export_col_contract),
            plannedHours = getString(Res.string.export_col_planned_hours),
            clockedHours = getString(Res.string.export_col_clocked_hours),
            paidHours = getString(Res.string.export_col_paid_hours),
            gross = getString(Res.string.export_col_gross),
            bonuses = getString(Res.string.export_col_bonuses),
            deductions = getString(Res.string.export_col_deductions),
            refunds = getString(Res.string.export_col_refunds),
            overtime50Hours = getString(Res.string.export_col_overtime_50),
            overtime100Hours = getString(Res.string.export_col_overtime_100),
            sundayHolidayHours = getString(Res.string.export_col_sunday_holiday),
            supplements = getString(Res.string.export_col_supplements),
            minimumTopUp = getString(Res.string.export_col_minimum_top_up),
            sickDays = getString(Res.string.export_col_sick_days),
            sickPay = getString(Res.string.export_col_sick_pay),
            vacationDays = getString(Res.string.export_col_vacation_days),
            holidayPay = getString(Res.string.export_col_holiday_pay),
            plannedGross = getString(Res.string.export_col_planned_gross),
            plannedEmployerCost = getString(Res.string.export_col_employer_cost_plan),
            employerCost = getString(Res.string.export_col_employer_cost),
            currency = getString(Res.string.export_col_currency),
            contractName = { contracts[it].orEmpty() }
        )
    }
}
